/**
 * Feature Flag Store
 *
 * Manages feature flag definitions, CRUD operations, and flag evaluation.
 * Persists to localStorage as the mock backend -- designed to swap to a
 * real API by replacing the persist layer with async API calls.
 *
 * Supports boolean, multivariate (A/B), and JSON flag types with targeting
 * rules based on role, user ID, account tier, percentage rollout, and environment.
 * Evaluation uses deterministic hashing so a user always gets the same result
 * for a given flag.
 *
 * @module useFeatureFlagStore
 *
 * @example
 * ```tsx
 * import { useFeatureFlagStore, selectFlagByKey } from '@/store/useFeatureFlagStore'
 *
 * function WalletFeature() {
 *   const { evaluateFlag } = useFeatureFlagStore()
 *   const walletFlag = useFeatureFlagStore(selectFlagByKey('wallet_v2'))
 *
 *   const result = evaluateFlag('wallet_v2', {
 *     userId: 'user-123',
 *     role: 'platform_admin',
 *     accountTier: 'standard',
 *     environment: 'production',
 *   })
 *
 *   return result.enabled ? <WalletV2 /> : <WalletV1 />
 * }
 * ```
 */

import { createWithEqualityFn } from 'zustand/traditional'
import { shallow } from 'zustand/shallow'
import { persist, createJSONStorage } from 'zustand/middleware'
import type {
  FeatureFlag,
  CreateFeatureFlagData,
  UpdateFeatureFlagData,
  FlagEvaluationContext,
  FlagEvaluationResult,
  FlagStatus,
  TargetingRule,
} from '../types'

// ============================================================================
// Helpers
// ============================================================================

/**
 * Deterministic hash of userId + flagKey to a 0-99 bucket.
 * Ensures a user always gets the same result for a given flag.
 *
 * @param userId - The user's unique identifier
 * @param flagKey - The feature flag's key string
 * @returns A number between 0 and 99 (inclusive) for percentage-based targeting
 */
function hashToPercentage(userId: string, flagKey: string): number {
  const str = `${userId}:${flagKey}`
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i)
    hash |= 0
  }
  return Math.abs(hash) % 100
}

function generateId(): string {
  return `ff_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
}

function now(): string {
  return new Date().toISOString()
}

/**
 * Evaluate a single targeting rule against a user context.
 *
 * @param rule - The targeting rule to evaluate
 * @param context - The user's evaluation context (userId, role, accountTier, environment)
 * @param flagKey - The flag key (used for percentage-based hashing)
 * @returns `true` if the rule passes for this context
 */
function evaluateRule(rule: TargetingRule, context: FlagEvaluationContext, flagKey: string): boolean {
  switch (rule.type) {
    case 'role': {
      if (rule.operator === 'in') return rule.values.includes(context.role)
      if (rule.operator === 'not_in') return !rule.values.includes(context.role)
      return false
    }
    case 'user_id': {
      if (rule.operator === 'in') return rule.values.includes(context.userId)
      if (rule.operator === 'not_in') return !rule.values.includes(context.userId)
      return false
    }
    case 'account_tier': {
      if (rule.operator === 'in') return rule.values.includes(context.accountTier)
      if (rule.operator === 'not_in') return !rule.values.includes(context.accountTier)
      return false
    }
    case 'percentage': {
      const threshold = parseInt(rule.values[0] ?? '0', 10)
      const bucket = hashToPercentage(context.userId, flagKey)
      return bucket < threshold
    }
    case 'environment': {
      if (rule.operator === 'in') return rule.values.includes(context.environment)
      if (rule.operator === 'not_in') return !rule.values.includes(context.environment)
      return false
    }
    default:
      return false
  }
}

// ============================================================================
// Seed data
// ============================================================================

const seedFlags: FeatureFlag[] = [
  {
    id: 'ff-seed-001',
    key: 'wallet_v2',
    name: 'Wallet V2',
    description: 'New wallet experience with transaction history and payout management.',
    type: 'boolean',
    status: 'active',
    defaultValue: true,
    targetingRules: [
      { id: 'rule-001', type: 'role', operator: 'in', values: ['platform_admin'] },
    ],
    tags: ['internal', 'wallet'],
    createdBy: 'System',
    createdAt: '2024-12-01T00:00:00Z',
    updatedAt: '2024-12-01T00:00:00Z',
  },
  {
    id: 'ff-seed-002',
    key: 'beta_challenges',
    name: 'Beta Challenges',
    description: 'Access to beta challenge features including new verification flow.',
    type: 'boolean',
    status: 'active',
    defaultValue: true,
    targetingRules: [
      { id: 'rule-002', type: 'percentage', operator: 'percentage_lte', values: ['25'] },
    ],
    tags: ['beta', 'challenges'],
    createdBy: 'System',
    createdAt: '2024-12-15T00:00:00Z',
    updatedAt: '2024-12-15T00:00:00Z',
  },
  {
    id: 'ff-seed-003',
    key: 'dark_mode',
    name: 'Dark Mode',
    description: 'Dark mode theme toggle. Currently in development.',
    type: 'boolean',
    status: 'inactive',
    defaultValue: false,
    targetingRules: [],
    tags: ['ui', 'theme'],
    createdBy: 'System',
    createdAt: '2025-01-10T00:00:00Z',
    updatedAt: '2025-01-10T00:00:00Z',
  },
  {
    id: 'ff-seed-005',
    key: 'discover_something_new',
    name: 'Discover Something New',
    description: 'Shows an Explore section with challenges from categories the user did NOT select during onboarding, encouraging curiosity about unfamiliar topics.',
    type: 'boolean',
    status: 'inactive',
    defaultValue: true,
    targetingRules: [],
    tags: ['explore', 'personalization'],
    createdBy: 'System',
    createdAt: '2026-02-21T00:00:00Z',
    updatedAt: '2026-02-21T00:00:00Z',
  },
  {
    id: 'ff-seed-004',
    key: 'new_onboarding',
    name: 'New Onboarding Flow',
    description: 'A/B test between the current onboarding and a streamlined version.',
    type: 'multivariate',
    status: 'active',
    defaultValue: true,
    variants: [
      { key: 'control', label: 'Current Flow', weight: 50, value: 'control' },
      { key: 'streamlined', label: 'Streamlined Flow', weight: 50, value: 'streamlined' },
    ],
    targetingRules: [
      { id: 'rule-003', type: 'role', operator: 'not_in', values: ['platform_admin', 'staff'] },
    ],
    tags: ['onboarding', 'ab-test'],
    createdBy: 'System',
    createdAt: '2025-02-01T00:00:00Z',
    updatedAt: '2025-02-01T00:00:00Z',
  },
]

// ============================================================================
// Store types
// ============================================================================

interface FeatureFlagState {
  flags: FeatureFlag[]
  isLoading: boolean
  error: string | null
}

interface FeatureFlagActions {
  fetchFlags: () => void
  createFlag: (data: CreateFeatureFlagData) => void
  updateFlag: (id: string, data: UpdateFeatureFlagData) => void
  deleteFlag: (id: string) => void
  toggleFlag: (id: string) => void
  evaluateFlag: (flagKey: string, context: FlagEvaluationContext) => FlagEvaluationResult
  clearError: () => void
}

type FeatureFlagStore = FeatureFlagState & FeatureFlagActions

const initialState: FeatureFlagState = {
  flags: seedFlags,
  isLoading: false,
  error: null,
}

// ============================================================================
// Store
// ============================================================================

export const useFeatureFlagStore = createWithEqualityFn<FeatureFlagStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      /**
       * Load flags. Currently a no-op since flags are persisted in localStorage.
       * Replace with API call when backend is ready.
       */
      fetchFlags: () => {
        // Flags are already hydrated from localStorage by persist middleware.
        // When switching to API: set({ isLoading: true }), fetch, then set results.
      },

      /**
       * Create a new feature flag.
       */
      createFlag: (data: CreateFeatureFlagData) => {
        const { flags } = get()

        // Check for duplicate key
        if (flags.some((f) => f.key === data.key)) {
          set({ error: `A flag with key "${data.key}" already exists.` })
          return
        }

        const timestamp = now()
        const newFlag: FeatureFlag = {
          id: generateId(),
          ...data,
          createdBy: 'Current Admin', // Will use real user when auth is wired
          createdAt: timestamp,
          updatedAt: timestamp,
        }

        set({ flags: [...flags, newFlag], error: null })
      },

      /**
       * Update an existing feature flag.
       */
      updateFlag: (id: string, data: UpdateFeatureFlagData) => {
        const { flags } = get()

        // Check for duplicate key if key is being changed
        if (data.key) {
          const existing = flags.find((f) => f.key === data.key && f.id !== id)
          if (existing) {
            set({ error: `A flag with key "${data.key}" already exists.` })
            return
          }
        }

        set({
          flags: flags.map((f) =>
            f.id === id
              ? { ...f, ...data, updatedAt: now(), updatedBy: 'Current Admin' }
              : f
          ),
          error: null,
        })
      },

      /**
       * Delete a feature flag.
       */
      deleteFlag: (id: string) => {
        set({ flags: get().flags.filter((f) => f.id !== id), error: null })
      },

      /**
       * Quick toggle a flag between active and inactive.
       */
      toggleFlag: (id: string) => {
        set({
          flags: get().flags.map((f) => {
            if (f.id !== id) return f
            const newStatus: FlagStatus = f.status === 'active' ? 'inactive' : 'active'
            return { ...f, status: newStatus, updatedAt: now(), updatedBy: 'Current Admin' }
          }),
        })
      },

      /**
       * Evaluate a flag for a given user context.
       * Returns { enabled, value } — the resolved state for this user.
       */
      evaluateFlag: (flagKey: string, context: FlagEvaluationContext): FlagEvaluationResult => {
        const flag = get().flags.find((f) => f.key === flagKey)

        // Flag not found or not active
        if (!flag || flag.status !== 'active') {
          return { enabled: false, value: flag?.defaultValue ?? false }
        }

        // No targeting rules → enabled for everyone
        if (flag.targetingRules.length === 0) {
          return { enabled: true, value: flag.defaultValue }
        }

        // All targeting rules must pass (AND logic)
        const allRulesPass = flag.targetingRules.every((rule) =>
          evaluateRule(rule, context, flagKey)
        )

        if (!allRulesPass) {
          return { enabled: false, value: flag.defaultValue }
        }

        // For multivariate flags, pick a variant based on hash
        if (flag.type === 'multivariate' && flag.variants && flag.variants.length > 0) {
          const bucket = hashToPercentage(context.userId, flagKey)
          let cumulative = 0
          for (const variant of flag.variants) {
            cumulative += variant.weight
            if (bucket < cumulative) {
              return { enabled: true, value: variant.value }
            }
          }
          // Fallback to last variant
          return { enabled: true, value: flag.variants[flag.variants.length - 1].value }
        }

        // For JSON flags, return the json payload
        if (flag.type === 'json' && flag.jsonValue) {
          return { enabled: true, value: flag.jsonValue }
        }

        // Boolean flag
        return { enabled: true, value: flag.defaultValue }
      },

      /**
       * Clear error state.
       */
      clearError: () => {
        set({ error: null })
      },
    }),
    {
      name: 'docurious-feature-flags',
      storage: createJSONStorage(() => localStorage),
    },
  ),
  shallow,
)

// ============================================================================
// Selectors
// ============================================================================

/** Select all feature flags (active and inactive). */
export const selectFlags = (state: FeatureFlagStore) => state.flags

/** Select only active feature flags. */
export const selectActiveFlags = (state: FeatureFlagStore) =>
  state.flags.filter((f) => f.status === 'active')

/**
 * Create a selector for a single flag by its unique key.
 *
 * @param key - The flag key to look up (e.g., `'wallet_v2'`)
 * @returns Selector returning the flag or `null` if not found
 *
 * @example
 * ```ts
 * const darkModeFlag = useFeatureFlagStore(selectFlagByKey('dark_mode'))
 * ```
 */
export const selectFlagByKey = (key: string) => (state: FeatureFlagStore) =>
  state.flags.find((f) => f.key === key) ?? null
